import { Extension } from '@tiptap/core';
import { Table } from '@tiptap/extension-table';
import { TableMap } from '@tiptap/pm/tables';
import { TextSelection } from '@tiptap/pm/state';
import { Fragment } from '@tiptap/pm/model';

// Table safety for the markdown editor — ported from Smartifakt (commit
// 6c8b116 there; see docs/EDITOR-UPSTREAM-FIXES.md). tiptap-markdown's stock
// table serializer bails to an HTML fallback whenever any cell holds more
// than one block node — and with html:false that fallback replaces the entire
// table with the literal text "[table]" in the saved markdown (a hardBreak in
// a cell likewise saves as literal "[hardBreak]"). Two layers of defense:
//
// 1. TableCellGuard keeps cells single-block at the keyboard: Enter moves to
//    the cell below (or exits below the table from the last row) instead of
//    splitting the paragraph; Shift/Mod-Enter are swallowed instead of
//    inserting a hardBreak.
// 2. MarkdownTable replaces the serializer's bail with flattening: blocks in
//    a multi-block cell are joined with spaces, hardBreaks and newlines
//    become spaces, stray images serialize inline as ![alt](src). This
//    catches content that reaches a cell past the keymap (multi-paragraph
//    paste, list/code input rules typed inside a cell).
//
// GFM has no syntax for block structure inside a cell, so flattening on save
// is the least-lossy option under html:false. Cells that are already
// single-paragraph serialize byte-identically to the stock serializer.

const CELL_TYPES = new Set(['tableCell', 'tableHeader']);

function cellDepth($pos) {
  for (let d = $pos.depth; d > 0; d--) {
    if (CELL_TYPES.has($pos.node(d).type.name)) return d;
  }
  return null;
}

// Markdown has no representation for a block image nested in a list item or
// table cell — tiptap-markdown drops the former silently, and the latter
// costs the whole table. Returns a top-level position to hop to when the
// target sits inside a list or table, else null.
export function imageInsertPos(state, pos) {
  const $pos = state.doc.resolve(pos);
  for (let d = $pos.depth; d > 0; d--) {
    const name = $pos.node(d).type.name;
    if (name === 'listItem' || name === 'taskItem' || name === 'table') return $pos.after(1);
  }
  return null;
}

export const TableCellGuard = Extension.create({
  name: 'tableCellGuard',
  priority: 1000, // must outrank hardBreak's Shift/Mod-Enter and the core Enter chain

  addKeyboardShortcuts() {
    const inCell = () => cellDepth(this.editor.state.selection.$from) !== null;
    return {
      Enter: () => {
        const { state } = this.editor;
        const { $from } = state.selection;
        const depth = cellDepth($from);
        if (depth === null) return false;
        const tableDepth = depth - 2; // cell < row < table
        const tableStart = $from.start(tableDepth);
        const map = TableMap.get($from.node(tableDepth));
        const rect = map.findCell($from.before(depth) - tableStart);
        return this.editor.commands.command(({ tr, dispatch }) => {
          if (!dispatch) return true;
          if (rect.bottom < map.height) {
            const below = tableStart + map.map[rect.bottom * map.width + rect.left];
            tr.setSelection(TextSelection.near(tr.doc.resolve(below), 1));
          } else {
            const after = $from.after(tableDepth);
            if (tr.doc.nodeAt(after)) {
              tr.setSelection(TextSelection.near(tr.doc.resolve(after), 1));
            } else {
              tr.insert(after, state.schema.nodes.paragraph.createAndFill());
              tr.setSelection(TextSelection.create(tr.doc, after + 1));
            }
          }
          dispatch(tr.scrollIntoView());
          return true;
        });
      },
      'Shift-Enter': inCell,
      'Mod-Enter': inCell,
    };
  },
});

export const MarkdownTable = Table.extend({
  addStorage() {
    return {
      markdown: {
        serialize: serializeTable,
        // parse stays with tiptap-markdown's default (markdown-it handles it)
      },
    };
  },
});

function serializeTable(state, node) {
  state.inTable = true;
  node.forEach((row, rowOffset, rowIndex) => {
    state.write('| ');
    row.forEach((cell, cellOffset, cellIndex) => {
      if (cellIndex) state.write(' | ');
      renderCellContent(state, cell);
    });
    state.write(' |');
    state.ensureNewLine();
    if (!rowIndex) {
      const delimiterRow = Array.from({ length: row.childCount })
        .map(() => '---')
        .join(' | ');
      state.write(`| ${delimiterRow} |`);
      state.ensureNewLine();
    }
  });
  state.closeBlock(node);
  state.inTable = false;
}

function renderCellContent(state, cell) {
  const blocks = [];
  collectCellBlocks(cell, blocks);
  blocks
    .filter((block) => block.type.name === 'image' || block.textContent.trim())
    .forEach((block, i) => {
      if (i) state.write(' ');
      if (block.type.name === 'image') writeInlineImage(state, block);
      else state.renderInline(flattenInline(block));
    });
}

function collectCellBlocks(node, out) {
  node.forEach((child) => {
    if (child.isTextblock || child.type.name === 'image') out.push(child);
    else collectCellBlocks(child, out);
  });
}

// A GFM table row is one physical line — hardBreaks and raw newlines inside a
// cell's text would split it, so they flatten to spaces.
function flattenInline(block) {
  const children = [];
  block.forEach((child) => {
    if (child.type.name === 'hardBreak') {
      children.push(block.type.schema.text(' '));
    } else if (child.isText && child.text.includes('\n')) {
      children.push(block.type.schema.text(child.text.replace(/\n/g, ' '), child.marks));
    } else {
      children.push(child);
    }
  });
  return block.copy(Fragment.from(children));
}

// Mirrors prosemirror-markdown's image serializer, minus the closeBlock a
// block-level image would get outside a table.
function writeInlineImage(state, node) {
  const src = node.attrs.src.replace(/[()]/g, '\\$&');
  const title = node.attrs.title ? ` "${node.attrs.title.replace(/"/g, '\\"')}"` : '';
  state.write(`![${state.esc(node.attrs.alt || '')}](${src}${title})`);
}
